import React from 'react';
import { Container, Section, Panel, Divider, EmptyState } from '../../components/layouts';
import { PrimaryButton } from '../../components/buttons';
import { GlobeIcon, PlusIcon } from '../../icons';

/**
 * LayoutShowcase
 * Displays layout components with examples
 */
import { surfaces, textColors, typography, borders } from '../../tokens';
const LayoutShowcase = () => {
  const CodeBlock = ({ code }) => (
    <pre className="bg-gray-900 dark:bg-dark-950 text-gray-100 dark:text-dark-200 p-4 rounded-lg text-xs overflow-x-auto">
      <code>{code}</code>
    </pre>
  );

  return (
    <div className="space-y-12">
      {/* Container */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Container</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Max-width content wrapper with automatic centering.
        </p>

        <div className="space-y-4 mb-6">
          <div className="bg-gray-100 p-1">
            <Container size="sm" className="bg-white py-4">
              <p className="text-center ${textColors.secondary}">Small Container (640px)</p>
            </Container>
          </div>

          <div className="bg-gray-100 p-1">
            <Container size="md" className="bg-white py-4">
              <p className="text-center ${textColors.secondary}">Medium Container (768px)</p>
            </Container>
          </div>

          <div className="bg-gray-100 p-1">
            <Container size="lg" className="bg-white py-4">
              <p className="text-center ${textColors.secondary}">Large Container (1024px)</p>
            </Container>
          </div>

          <div className="bg-gray-100 p-1">
            <Container size="xl" className="bg-white py-4">
              <p className="text-center ${textColors.secondary}">XL Container (1280px)</p>
            </Container>
          </div>

          <div className="bg-gray-100 p-1">
            <Container size="2xl" className="bg-white py-4">
              <p className="text-center ${textColors.secondary}">2XL Container (1536px)</p>
            </Container>
          </div>

          <div className="bg-gray-100 p-1">
            <Container size="7xl" className="bg-white py-4">
              <p className="text-center ${textColors.secondary}">7XL Container (1920px) - Default for app</p>
            </Container>
          </div>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { Container } from '../design-system';

<Container size="7xl" className="py-8">
  <h1>Page Content</h1>
</Container>`} />
        </div>
      </section>

      {/* Section */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Section</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Semantic section component with optional title and description.
        </p>

        <div className="space-y-6 mb-6">
          <Section
            title="Features"
            description="Explore our amazing features"
            background="white"
            padding="lg"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="font-medium ${textColors.brightest}">Feature 1</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="font-medium ${textColors.brightest}">Feature 2</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="font-medium ${textColors.brightest}">Feature 3</p>
              </div>
            </div>
          </Section>

          <Section
            title="Pricing"
            background="gray"
            padding="md"
          >
            <p className="${textColors.secondary}">Section with gray background</p>
          </Section>

          <Section padding="sm">
            <p className="${textColors.secondary}">Section without title (small padding)</p>
          </Section>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { Section } from '../design-system';

<Section
  title="Features"
  description="Explore our features"
  background="white"
  padding="lg"
>
  Section content
</Section>`} />
        </div>
      </section>

      {/* Panel */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Panel</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Versatile panel for grouping content with configurable styling.
        </p>

        <div className="space-y-4 mb-6">
          <Panel background="white" border rounded shadow="sm" padding="md">
            <h3 className="font-heading font-bold ${textColors.brightest} mb-2">Default Panel</h3>
            <p className="${textColors.secondary}">
              Panel with white background, border, rounded corners, and small shadow.
            </p>
          </Panel>

          <Panel background="gray" border rounded padding="lg">
            <h3 className="font-heading font-bold ${textColors.brightest} mb-2">Gray Panel</h3>
            <p className="${textColors.secondary}">
              Panel with gray background and large padding.
            </p>
          </Panel>

          <Panel background="blue" rounded shadow="md" padding="md">
            <h3 className="font-heading font-bold text-white mb-2">Blue Panel</h3>
            <p className="text-blue-50">
              Panel with blue background and medium shadow (no border).
            </p>
          </Panel>

          <Panel background="gradient" rounded shadow="lg" padding="lg">
            <h3 className="font-heading font-bold text-white mb-2">Gradient Panel</h3>
            <p className="text-white">
              Panel with gradient background and large shadow.
            </p>
          </Panel>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { Panel } from '../design-system';

<Panel 
  background="white" 
  border 
  rounded 
  shadow="sm" 
  padding="md"
>
  Panel content
</Panel>`} />
        </div>
      </section>

      {/* Divider */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Divider</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Horizontal separator with optional label.
        </p>

        <div className="space-y-6 mb-6 bg-white p-6 rounded-lg border border-gray-200">
          <div>
            <p className="${textColors.secondary}">Content above divider</p>
            <Divider variant="solid" spacing="md" />
            <p className="${textColors.secondary}">Content below divider</p>
          </div>

          <div>
            <p className="${textColors.secondary}">With label</p>
            <Divider label="OR" variant="solid" spacing="md" />
            <p className="${textColors.secondary}">Another section</p>
          </div>

          <div>
            <p className="${textColors.secondary}">Dashed variant</p>
            <Divider variant="dashed" spacing="lg" />
            <p className="${textColors.secondary}">Another section</p>
          </div>

          <div>
            <p className="${textColors.secondary}">Dotted variant</p>
            <Divider variant="dotted" spacing="sm" />
            <p className="${textColors.secondary}">Another section</p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { Divider } from '../design-system';

<Divider label="OR" variant="solid" spacing="md" />`} />
        </div>
      </section>

      {/* EmptyState */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>EmptyState</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Display for no data scenarios with icon, title, description, and action.
        </p>

        <div className="space-y-6 mb-6">
          <div className="bg-white p-8 rounded-lg border border-gray-200">
            <EmptyState
              icon={<GlobeIcon size="3xl" color="primary" />}
              title="No websites yet"
              description="Create your first website to get started with MyVitae"
              action={
                <PrimaryButton leftIcon={<PlusIcon size="sm" />}>
                  Create Website
                </PrimaryButton>
              }
            />
          </div>

          <div className="bg-white p-8 rounded-lg border border-gray-200">
            <EmptyState
              title="No results found"
              description="Try adjusting your search or filters"
            />
          </div>

          <div className="bg-gray-50 p-8 rounded-lg">
            <EmptyState
              icon={<GlobeIcon size="2xl" color="neutral" />}
              title="Coming soon"
              description="This feature is currently under development"
            />
          </div>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { EmptyState } from '../design-system';

<EmptyState
  icon={<GlobeIcon size="3xl" color="primary" />}
  title="No items found"
  description="Create your first item to get started"
  action={
    <PrimaryButton onClick={handleCreate}>
      Create Item
    </PrimaryButton>
  }
/>`} />
        </div>
      </section>

      {/* Layout Composition Example */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Layout Composition</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Example of combining layout components.
        </p>

        <div className="mb-6">
          <Container size="xl">
            <Section
              title="Dashboard"
              description="Welcome back to your dashboard"
              background="white"
              padding="lg"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Panel background="white" border rounded shadow="sm" padding="md">
                  <h3 className="font-heading font-bold ${textColors.brightest} mb-2">Statistics</h3>
                  <p className="${textColors.secondary}">View your performance metrics</p>
                </Panel>

                <Panel background="white" border rounded shadow="sm" padding="md">
                  <h3 className="font-heading font-bold ${textColors.brightest} mb-2">Activity</h3>
                  <p className="${textColors.secondary}">Recent activity on your account</p>
                </Panel>
              </div>

              <Divider spacing="lg" />

              <Panel background="gray" rounded padding="md">
                <p className="${textColors.secondary} text-center">
                  Additional dashboard content
                </p>
              </Panel>
            </Section>
          </Container>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { Container, Section, Panel, Divider } from '../design-system';

<Container size="xl">
  <Section title="Dashboard" padding="lg">
    <Panel background="white" border rounded>
      Panel content
    </Panel>
    
    <Divider spacing="lg" />
    
    <Panel background="gray" rounded>
      More content
    </Panel>
  </Section>
</Container>`} />
        </div>
      </section>
    </div>
  );
};

export default LayoutShowcase;
